import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, MessageSquareText, ListTodo } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import RichTextEditor from '@/components/RichTextEditor';
import RichTextDisplay from '@/components/RichTextDisplay';
import CreateTodoFromNoteDialog from './CreateTodoFromNoteDialog';
import MeetingNoteTodosBadge from '@/components/MeetingNoteTodosBadge';

/**
 * Client-scoped meeting note modal.
 *
 * Flow:
 *   1. Pick a client from the dropdown.
 *   2. The project list refreshes to that client's projects (all checked by default).
 *   3. Fill date / attendees / rich-text notes.
 *   4. Save -> POST /api/meeting-notes/client
 *
 * Below the form, the existing client-linked notes for the selected client are
 * listed with edit + delete actions (delete lives here exclusively per product spec).
 */
export default function ClientMeetingNoteModal({ open, onOpenChange, clients, projects, users, onSaved }) {
  const emptyForm = { date: new Date().toISOString().slice(0, 10), attendees: '', notes: '' };
  const [clientId, setClientId] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [existingNotes, setExistingNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', attendees: '', notes: '', project_ids: [] });
  const [todoFromNote, setTodoFromNote] = useState(null);

  const clientProjects = projects.filter(p => p.client_id === clientId && p.status !== 'Archived');

  // For the edit form, union active client projects with any currently-scoped
  // projects (including archived) so we never silently drop a project that was
  // archived AFTER it was added to the note. Active projects come first.
  const getEditProjects = (currentIds = []) => {
    const activeIds = new Set(clientProjects.map(p => p.id));
    const preserved = projects.filter(p => currentIds.includes(p.id) && !activeIds.has(p.id));
    return [...clientProjects, ...preserved];
  };

  
  useEffect(() => {
    if (!open) {
      // Reset on close
      setClientId('');
      setSelectedProjectIds([]);
      setForm(emptyForm);
      setExistingNotes([]);
      setEditingNoteId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!clientId) {
      setSelectedProjectIds([]);
      setExistingNotes([]);
      return;
    }
    // Default-select all of this client's active projects
    setSelectedProjectIds(clientProjects.map(p => p.id));
    // Fetch existing client-linked notes for manage list
    setLoadingNotes(true);
    api.get(`/meeting-notes/client/${clientId}`)
      .then(r => setExistingNotes(r.data || []))
      .catch(() => toast.error('Failed to load existing client notes'))
      .finally(() => setLoadingNotes(false));
  }, [clientId]);
  

  const toggleProject = (pid) => {
    setSelectedProjectIds(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };

  const save = async () => {
    if (!clientId) return toast.error('Select a client');
    if (selectedProjectIds.length === 0) return toast.error('Select at least one project');
    if (!form.notes || !form.notes.replace(/<[^>]+>/g, '').trim()) return toast.error('Notes are required');
    setSaving(true);
    try {
      await api.post('/meeting-notes/client', {
        client_id: clientId,
        project_ids: selectedProjectIds,
        date: form.date,
        attendees: form.attendees,
        notes: form.notes,
      });
      toast.success(`Meeting note added to ${selectedProjectIds.length} project(s)`);
      setForm(emptyForm);
      // Refresh list + notify parent to refetch projects / detail pane
      const r = await api.get(`/meeting-notes/client/${clientId}`);
      setExistingNotes(r.data || []);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save meeting note');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (n) => {
    setEditingNoteId(n.id);
    setEditForm({
      date: n.date || '',
      attendees: n.attendees || '',
      notes: n.notes || '',
      project_ids: [...(n.project_ids || [])],
    });
  };

  const saveEdit = async () => {
    if (!editForm.notes || !editForm.notes.replace(/<[^>]+>/g, '').trim()) return toast.error('Notes are required');
    if (!editForm.project_ids || editForm.project_ids.length === 0) return toast.error('At least one project must be selected');
    const note = existingNotes.find(n => n.id === editingNoteId);
    const originalIds = (note?.project_ids || []).slice().sort().join(',');
    const newIds = editForm.project_ids.slice().sort().join(',');
    // Pick ANY project from the new list as the URL param (edit endpoint accepts
    // either the original project_id OR any id currently in project_ids).
    const anyProject = editForm.project_ids[0];
    try {
      // If the project scope changed, update scope FIRST so the content PUT
      // lands on a still-valid project id.
      if (originalIds !== newIds) {
        await api.put(`/meeting-notes/${editingNoteId}/scope`, { project_ids: editForm.project_ids });
      }
      await api.put(`/projects/${anyProject}/meeting-notes/${editingNoteId}`, {
        date: editForm.date,
        attendees: editForm.attendees,
        notes: editForm.notes,
      });
      toast.success('Meeting note updated');
      setEditingNoteId(null);
      const r = await api.get(`/meeting-notes/client/${clientId}`);
      setExistingNotes(r.data || []);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    }
  };

  const toggleEditProject = (pid) => {
    setEditForm(prev => ({
      ...prev,
      project_ids: prev.project_ids.includes(pid)
        ? prev.project_ids.filter(x => x !== pid)
        : [...prev.project_ids, pid],
    }));
  };

  const del = async (noteId) => {
    if (!window.confirm('Delete this client meeting note? It will be removed from every project it appears on.')) return;
    try {
      await api.delete(`/meeting-notes/${noteId}`);
      toast.success('Meeting note deleted');
      setExistingNotes(prev => prev.filter(n => n.id !== noteId));
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-meeting-note-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareText className="h-5 w-5 text-primary" />
            Add Client Meeting Note
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Attach one note to multiple projects that share a client.
          </p>
        </DialogHeader>

        <div className="space-y-3">
          {/* Client picker */}
          <div>
            <Label className="text-xs text-muted-foreground">Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="bg-background border-border" data-testid="client-meeting-note-client">
                <SelectValue placeholder="Select a client..." />
              </SelectTrigger>
              <SelectContent className="bg-card border-border max-h-72">
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id} className="text-sm">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Project multi-select */}
          {clientId && (
            <div>
              <Label className="text-xs text-muted-foreground">
                Projects in this meeting * ({selectedProjectIds.length} of {clientProjects.length})
              </Label>
              {clientProjects.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">This client has no active projects.</p>
              ) : (
                <div className="border border-border rounded-md p-2 max-h-48 overflow-y-auto space-y-1 bg-background" data-testid="client-meeting-note-projects">
                  {clientProjects.map(p => (
                    <label key={p.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-secondary/30 cursor-pointer text-sm">
                      <Checkbox
                        checked={selectedProjectIds.includes(p.id)}
                        onCheckedChange={() => toggleProject(p.id)}
                        data-testid={`client-meeting-note-project-${p.id}`}
                      />
                      <span className="font-mono text-xs text-primary">{p.project_number}</span>
                      <span className="text-xs">{p.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Note form */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                className="bg-background border-border"
                data-testid="client-meeting-note-date"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Attendees</Label>
              <Input
                placeholder="e.g. John, Sarah, Mike"
                value={form.attendees}
                onChange={e => setForm({ ...form, attendees: e.target.value })}
                className="bg-background border-border"
                data-testid="client-meeting-note-attendees"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Notes *</Label>
            <RichTextEditor
              placeholder="Meeting notes..."
              value={form.notes}
              onChange={v => setForm({ ...form, notes: v })}
              data-testid="client-meeting-note-content"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || !clientId || selectedProjectIds.length === 0}
            className="bg-primary text-primary-foreground"
            data-testid="save-client-meeting-note-btn"
          >
            <Plus className="h-3 w-3 mr-1" />
            {saving ? 'Saving…' : 'Save Meeting Note'}
          </Button>
        </DialogFooter>

        {/* Existing client-linked notes for selected client */}
        {clientId && (
          <div className="border-t border-border pt-3 mt-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground">
                Existing client notes ({existingNotes.length})
              </p>
            </div>
            {loadingNotes ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : existingNotes.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No client meeting notes yet.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {existingNotes.map(n => (
                  <Card key={n.id} className="border-border bg-background shadow-none" data-testid={`existing-client-note-${n.id}`}>
                    <CardContent className="p-3">
                      {editingNoteId === n.id ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Input type="date" value={editForm.date} onChange={e => setEditForm({ ...editForm, date: e.target.value })} className="bg-background border-border" />
                            <Input placeholder="Attendees" value={editForm.attendees} onChange={e => setEditForm({ ...editForm, attendees: e.target.value })} className="bg-background border-border" />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">
                              Projects in scope ({editForm.project_ids.length} of {getEditProjects(editForm.project_ids).length})
                            </Label>
                            {getEditProjects(editForm.project_ids).length === 0 ? (
                              <p className="text-xs text-muted-foreground py-1">This client has no active projects.</p>
                            ) : (
                              <div className="border border-border rounded-md p-2 max-h-40 overflow-y-auto space-y-1 bg-background" data-testid={`edit-scope-${n.id}`}>
                                {getEditProjects(editForm.project_ids).map(p => (
                                  <label key={p.id} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-secondary/30 cursor-pointer text-sm">
                                    <Checkbox
                                      checked={editForm.project_ids.includes(p.id)}
                                      onCheckedChange={() => toggleEditProject(p.id)}
                                      data-testid={`edit-scope-${n.id}-project-${p.id}`}
                                    />
                                    <span className="font-mono text-xs text-primary">{p.project_number}</span>
                                    <span className="text-xs">{p.name}</span>
                                    {p.status === 'Archived' && (
                                      <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-600 ml-auto">Archived</Badge>
                                    )}
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                          <RichTextEditor value={editForm.notes} onChange={v => setEditForm({ ...editForm, notes: v })} />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={saveEdit} className="bg-primary text-primary-foreground" data-testid={`save-edit-client-note-${n.id}`}>Save</Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingNoteId(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <Badge variant="outline" className="text-xs font-mono">{n.date}</Badge>
                              <Badge className="text-xs bg-blue-500/15 text-blue-600 hover:bg-blue-500/15 border border-blue-500/30">
                                {(n.project_ids || []).length} project{(n.project_ids || []).length === 1 ? '' : 's'}
                              </Badge>
                              <MeetingNoteTodosBadge note={n} />
                              {n.attendees && <span className="text-xs text-muted-foreground">Attendees: {n.attendees}</span>}
                            </div>
                            {n.project_labels && n.project_labels.length > 0 && (
                              <p className="text-xs text-muted-foreground mb-1 truncate">
                                {n.project_labels.join(' · ')}
                              </p>
                            )}
                            <RichTextDisplay value={n.notes} collapsedHeight={60} />
                            <p className="text-xs text-muted-foreground mt-1.5">
                              {n.created_by_name && `${n.created_by_name} · `}{new Date(n.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => setTodoFromNote(n)} data-testid={`create-todo-from-existing-note-${n.id}`} title="Create to-do">
                              <ListTodo className="h-3 w-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => openEdit(n)} data-testid={`edit-existing-client-note-${n.id}`}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => del(n.id)} data-testid={`delete-existing-client-note-${n.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>

      <CreateTodoFromNoteDialog
        open={!!todoFromNote}
        onOpenChange={(o) => { if (!o) setTodoFromNote(null); }}
        note={todoFromNote}
        users={users}
        onCreated={async () => {
          // Refresh the modal's existing-notes list so todo_count badges update
          if (clientId) {
            try {
              const r = await api.get(`/meeting-notes/client/${clientId}`);
              setExistingNotes(r.data || []);
            } catch { /* best-effort */ }
          }
          if (onSaved) onSaved();
        }}
      />
    </Dialog>
  );
}
